import mock from "mock/lib/Mock";
import lockClient from "mock/lib/lockClient";

/*
This configures the mocking subsystem that can use a mockset instead of the
real API to "service" requests. Order matters; patterns are searched in the
declared order and the first match wins, even if a more specific pattern is
declared farther down.
*/

const newIdOnPostFilter = function(data, req, config) {
    const postData = JSON.parse(config.data);
    data.newIds[postData.id] = data.id;
    return data;
};

const noOpPostProcessor = function(url) {
    return "/noop.json?" + url;
};

/**
 * I return a post-processor which will ensure the params in the URL are as
 * expected. By default, no params are allowed. Required and optional param
 * names may be specified, as well as passing a flag to allow arbitrary extra
 * params. Note that the `_` parameter the system appends behind the scenes for
 * cache control is exempt from the checks.
 * @param requiredParams An array of parameter names that must be provided.
 * @param optionalParams And array of parameter names that may be provided.
 * @param allowExtras Whether to allow extra parameters to be provided.
 * @return {function(*, *): *} The post-processor to wire onto the request.
 */
function paramValidatingPostProcessor(requiredParams=[], optionalParams=[], allowExtras=false) {
    return (url, xhr) => {
        const names = xhr.url
            .split("?")
            .slice(1)
            .join("?")
            .split("&")
            .map(pair => {
                const segments = pair.split("=");
                const name = segments.shift();
                const value = segments.join("=");
                return [
                    decodeURIComponent(name),
                    decodeURIComponent(value),
                ];
            })
            .map(([name]) => name)
            .filter(n => n !== "_");
        requiredParams.forEach(n => {
            if (!names.includes(n)) {
                throw new Error(`Required param '${n}' was not provided to ${url} (params: ${names.join(",")})`);
            }
        });
        if (!allowExtras) {
            names.forEach(n => {
                if (!requiredParams.includes(n) && !optionalParams.includes(n)) {
                    throw new Error(`Extra param '${n}' was provided to ${url} (params: ${names.join(",")})`);
                }
            });
        }
        return url;
    };
}

// system
mock.GET("/meta/armusscript.js");
mock.GET("/broadcast");
mock.GET("/user");
mock.PUT("/user/active-organizations");
mock.POST("/user/active-organizations");
mock.GET("/user/prefs/{org}/{pref}");
mock.PUT("/user/prefs/{org}/{pref}");
mock.POST("/user/prefs/{org}/{pref}");
mock.GET("/prefs/{org}/{impl}/{pref}");
mock.PUT("/prefs/{org}/{impl}/{pref}");
mock.POST("/prefs/{org}/{impl}/{pref}");
mock.GET("/gxep/{org}");
mock.PUT( "/gxep/{org}").suffixed("POST");
// ADT remoting
mock.GET("/remote/data-source/adt/{org}/search");
// Hemo remoting
mock.GET("/remote/data-source/hemo/{org}/search");
// exports
mock.GET("/export/{org}/saved-params");
mock.POST("/export/{org}/saved-params");
mock.PUT("/export/{org}/saved-params/{id}");
mock.DELETE("/export/{org}/saved-params/{id}");
mock.POST("/export/{org}/generic-xml/export");
mock.GET("/export/{org}/history");
// metadata
mock.GET("/meta/version");
mock.GET("/meta/stereotype");
mock.GET("/meta/stereotype/{stereo}");
mock.GET("/meta/stereotype/{impl}/{version}/{entity}");
mock.GET("/meta/{org}/{stereo}/model.json");
mock.GET("/meta/{org}/{impl}/{version}/{entity}/model.json");
mock.GET("/meta/{org}/{impl}/{version}/{entity}/form.json");
mock.GET("/meta/{org}/{impl}/{version}/{entity}/form").extension("json");
mock.GET("/meta/{org}/{impl}/{version}/{entity}/fields/{field}");
mock.GET("/meta/{org}/{impl}/{version}/{entity}/mapping-src/{srcImpl}/{srcVersion}/{srcEntity}.json");
mock.GET("/meta/{org}/{impl}");
mock.GET("/meta/{org}/{impl}/{version}");
// occurrence data
mock.GET("/dqr/{stereo}").paged();
mock.GET("/dqr/{stereo}/fields").paged();
mock.GET("/search/{stereo}").paged();
mock.GET("/search/{impl}/{version}/{entity}").paged();
mock.GET("/{org}/{stereo}");
mock.POST("/{org}/{stereo}").suffixed("POST").filtered(newIdOnPostFilter);
mock.GET("/{org}/{stereo}/{id:i}");
mock.PUT("/{org}/{stereo}/{id:i}");
mock.POST("/{org}/{stereo}/{id:i}");
mock.DELETE("/{org}/{stereo}/{id:i}");
mock.GET("/{org}/{impl}/{version}/{entity}");
mock.POST("/{org}/{impl}/{version}/{entity}").suffixed("POST").filtered(newIdOnPostFilter);
mock.GET("/{org}/{impl}/{version}/{entity}/{id:i}");
mock.PUT("/{org}/{impl}/{version}/{entity}/{id:i}");
mock.POST("/{org}/{impl}/{version}/{entity}/{id:i}");
mock.DELETE("/{org}/{impl}/{version}/{entity}/{id:i}");
mock.POST("/{org}/{impl}/{version}/suggest/{field}");
// lock monitoring - all fake!
mock.POST("/lock")
    .postprocess(noOpPostProcessor)
    .filtered(() => lockClient.createClient());
mock.POST("/lock/{clientId}/set")
    .postprocess(noOpPostProcessor)
    .filtered((data, req, config) => lockClient.createLockSet(JSON.parse(config.data)));
mock.PUT("/lock/{clientId}/set/{locksetId}/active")
    .postprocess(noOpPostProcessor)
    .filtered((data, req) => lockClient.confirmLockSet(req.split("/")[4]));
mock.DELETE("/lock/{clientId}/set/{locksetId}")
    .postprocess(noOpPostProcessor)
    .filtered((data, req) => lockClient.releaseLockSet(req.split("/")[4]));
mock.DELETE("/lock/{clientId}")
    .postprocess(noOpPostProcessor)
    .filtered((data, req) => lockClient.releaseClient(req.split("/")[2]));

/*
 * Exporters don't actually exist, so spit back some static answers
 */
// shared by all
const participantOptions = [{
    hospname: "Certification Hospital",
    particid: "999999",
}, {
    hospname: "Test Hospital",
    particid: "12345",
}];
mock.GET("/export/{org}/{impl}/{type}/participants")
    .postprocess(noOpPostProcessor)
    .filtered(() => participantOptions);
mock.GET("/export/{org}/{impl}/{type}/date-range")
    .postprocess(noOpPostProcessor)
    .filtered((data, req, xhr) => {
        if (xhr.url.indexOf("12345") >= 0) {
            return {
                from: [2016, 2, 17],
                to: [2021, 4, 12],
            };
        }
        return {
            from: [2018, 4, 1],
            to: [2020, 11, 10],
        };
    });
const fieldOptions = {
    entities: [{
        fields: [{
            key: "ageatadmit",
            name: "Age at Admission",
            type: "integer",
        }, {
            key: "admitdate",
            name: "Date of Admission",
            type: "date",
        }],
        key: "episode",
        name: "Episode",
    }, {
        fields: [{
            key: "dob",
            name: "Date Of Birth",
            type: "date",
        }, {
            key: "race",
            name: "Race",
            type: "choice",
        }, {
            key: "sex",
            name: "Sex",
            type: "choice",
        }],
        key: "patient",
        name: "Patient",
    }, {
        fields: [{
            key: "surgid",
            name: "Surgeon ID",
            type: "string",
        }],
        key: "surgeon",
        name: "Surgeon",
    }],
    key: "sts-adult-cardiac-surgery",
    name: "Adult Cardiac Surgery",
};
// STS Harvests
mock.GET("/export/{org}/adult-cardiac/harvest/optional-fields")
    .postprocess(noOpPostProcessor)
    .filtered(() => fieldOptions);
mock.GET("/export/{org}/congenital/harvest/optional-fields")
    .postprocess(noOpPostProcessor)
    .filtered(() => fieldOptions);
mock.GET("/export/{org}/thoracic/harvest/optional-fields")
    .postprocess(noOpPostProcessor)
    .filtered(() => fieldOptions);
// Adult Cardiac Exporter
mock.GET("/export/{org}/adult-cardiac/export/exportable/hospitals")
    .postprocess(paramValidatingPostProcessor(
        [],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(() => participantOptions);
mock.GET("/export/{org}/adult-cardiac/export/exportable/surgdt-range")
    .postprocess(paramValidatingPostProcessor(
        ["particid"],
        ["datavrsns", "surgeons"],
    ))
    .postprocess(noOpPostProcessor)
    .filtered((data, req, xhr) => {
        if (xhr.url.indexOf("Physician A") >= 0) {
            return {
                from: [2021, 2, 17],
                to: [2021, 4, 12],
            };
        }
        if (xhr.url.indexOf("4.20.2") >= 0) {
            return {
                from: [2020, 2, 17],
                to: [2021, 4, 12],
            };
        }
        if (xhr.url.indexOf("12345") >= 0) {
            return {
                from: [2016, 2, 17],
                to: [2021, 4, 12],
            };
        }
        return {
            from: [2018, 4, 1],
            to: [2020, 11, 10],
        };
    });
mock.GET("/export/{org}/adult-cardiac/export/exportable/fields")
    .postprocess(paramValidatingPostProcessor(
        [],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(() => [
        {
            fields: [
                {
                    "entity": "episode",
                    "key": "case_id",
                    "name": "ARMUS Case ID",
                    "type": "STRING"
                },
                {
                    "entity": "patient",
                    "key": "adminsectionnotes",
                    "name": "Administrative Section Notes",
                    "type": "STRING"
                }
            ],
            name: "A. Administrative"
        },
        {
            fields: [
                {
                    "entity": "patient",
                    "key": "custom_armuscustompatient01",
                    "name": "ARMUS Custom Patient 01",
                    "type": "STRING"
                },
                {
                    "entity": "episode",
                    "key": "patcountry",
                    "name": "Patient's Country",
                    "type": "CHOICE"
                }
            ],
            name: "B. Demographics"
        },
    ]);
const filteredDatavrsns = (data, req, xhr) => {
    if (xhr.url.indexOf("12345") >= 0) {
        return [
            {"name": "2.9"},
            {"name": "1.0"},
        ];
    }
    return [
        {"name": "4.20.2"},
        {"name": "2.9"},
        {"name": "2.81"},
    ];
};
const filteredSurgeons = (data, req, xhr) => {
    if (xhr.url.indexOf("4.20.2") >= 0) {
        return [
            {"name": "Surgeons A", "npi": "12345"},
        ];
    }
    if (xhr.url.indexOf("12345") >= 0) {
        return [
            {"name": "Surgeons C", "npi": null},
        ];
    }
    return [
        {"name": "Surgeons A", "npi": "12345"},
        {"name": "Surgeons B", "npi": "99999"},
        {"name": "Surgeons C", "npi": null},
    ];
};
mock.GET("/export/{org}/adult-cardiac/export/exportable/datavrsns")
    .postprocess(paramValidatingPostProcessor(
        ["particid"],
        ["surgeons"],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(filteredDatavrsns);
mock.GET("/export/{org}/adult-cardiac/export/exportable/surgeons")
    .postprocess(paramValidatingPostProcessor(
        ["particid"],
        ["datavrsns", "surgdtStart", "surgdtEnd"],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(filteredSurgeons);
mock.GET("/export/{org}/adult-cardiac/export/exportable/procedure-types")
    .postprocess(noOpPostProcessor)
    .filtered(() => ["Cheese", "Sauce", "Pepperoni"].map(name => ({name})));
mock.GET("/export/{org}/thoracic/export/exportable/datavrsns")
    .postprocess(paramValidatingPostProcessor(
        ["particid"],
        ["surgeons"],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(filteredDatavrsns);
mock.GET("/export/{org}/thoracic/export/exportable/surgeons")
    .postprocess(paramValidatingPostProcessor(
        ["particid"],
        ["datavrsns"],
    ))
    .postprocess(noOpPostProcessor)
    .filtered(filteredSurgeons);




// WEBPACK FOOTER //
// ./test/mock/lib/mock_config.js